import { useMemo, useState } from "react";
import {
  HYSA_RATES,
  HYSA_RATES_AS_OF,
} from "../../lib/data/hysa-rates.js";
import {
  compareHysa,
  isHysaSnapshotStale,
} from "../../lib/util/hysa.js";
import {
  Card,
  Pill,
  SectionHeader,
  TextField,
} from "../../components/design-system/index.js";

/**
 * /banking/hysa — Phase 8 Session 3.
 *
 * User enters their current savings balance + APY → we rank the
 * curated HYSA list by APY, drop accounts whose `min_balance` exceeds
 * the user's balance, and surface annualized opportunity cost vs the
 * user's current rate.
 */

export function BankingHysaPage() {
  const [balanceDollars, setBalanceDollars] = useState<string>("25000");
  const [currentApyPct, setCurrentApyPct] = useState<string>("0.50");

  const balanceCents = useMemo(() => {
    const n = Number.parseFloat(balanceDollars);
    return Number.isFinite(n) ? Math.round(n * 100) : 0;
  }, [balanceDollars]);
  const currentApyBp = useMemo(() => {
    const n = Number.parseFloat(currentApyPct);
    return Number.isFinite(n) ? Math.round(n * 100) : 0;
  }, [currentApyPct]);

  const ranked = useMemo(
    () =>
      compareHysa({
        user_balance_cents: balanceCents,
        current_apy_bp: currentApyBp,
        comparators: HYSA_RATES,
      }),
    [balanceCents, currentApyBp],
  );

  const isStale = isHysaSnapshotStale(HYSA_RATES_AS_OF);

  return (
    <div className="mx-auto max-w-4xl p-6">
      <SectionHeader
        title="HYSA rate comparison"
        sub={`Snapshot as of ${HYSA_RATES_AS_OF}.${isStale ? " Snapshot more than 6 months old — verify rates before acting." : ""}`}
      />
      <Card className="mb-4">
        <div className="flex flex-wrap gap-3">
          <TextField
            label="Your current balance ($)"
            value={balanceDollars}
            onChange={(e) => setBalanceDollars(e.target.value)}
            type="number"
            min={0}
          />
          <TextField
            label="Your current APY (%)"
            value={currentApyPct}
            onChange={(e) => setCurrentApyPct(e.target.value)}
            type="number"
            step="0.01"
            min={0}
          />
        </div>
      </Card>

      <div className="space-y-2">
        {ranked.map((row) => {
          const c = row.comparator;
          const better = row.delta_bp > 0;
          return (
            <Card key={`${c.issuer}:${c.name}`}>
              <div className="flex items-start justify-between gap-3">
                <div>
                  <div className="text-sm font-semibold">{c.name}</div>
                  <div className="mt-1 flex flex-wrap items-center gap-2 text-xs text-[var(--color-muted)]">
                    <span>{c.issuer}</span>
                    {c.fdic_insured ? (
                      <Pill tone="success">FDIC</Pill>
                    ) : (
                      <Pill tone="warning">SIPC (MMF)</Pill>
                    )}
                    {c.min_balance_cents > 0 && (
                      <span>
                        min ${(c.min_balance_cents / 100).toFixed(0)}
                      </span>
                    )}
                  </div>
                  {c.notes && (
                    <p className="mt-1 text-xs text-[var(--color-muted)]">
                      {c.notes}
                    </p>
                  )}
                </div>
                <div className="text-right">
                  <div className="text-xs text-[var(--color-muted)]">APY</div>
                  <div className="font-mono text-lg font-semibold">
                    {(c.apy_bp / 100).toFixed(2)}%
                  </div>
                  {better ? (
                    <div className="text-xs text-[var(--color-success)]">
                      +${(row.annual_opportunity_cents / 100).toFixed(0)} /
                      yr at your balance
                    </div>
                  ) : (
                    <div className="text-xs text-[var(--color-muted)]">
                      {row.delta_bp === 0 ? "≈ your APY" : "below your APY"}
                    </div>
                  )}
                </div>
              </div>
            </Card>
          );
        })}
      </div>
    </div>
  );
}
